import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../contexts/AuthContext';
import { Carregando, dataHora, diasDesde } from '../components/analiseBanco/comum';
import Resumo from '../components/analiseBanco/Resumo';
import { ListaProblemas, ProblemaDetalhe } from '../components/analiseBanco/Problemas';
import Consultas, { ModalConsulta } from '../components/analiseBanco/Consultas';
import Deadlocks from '../components/analiseBanco/Deadlocks';
import Servidor from '../components/analiseBanco/Servidor';

// Analise do Banco de Dados (servidor SQL 192.168.0.55).
//
// Uma pergunta por nivel:
//   Resumo   — o que esta errado e quem resolve (30 segundos);
//   Problema — onde e quando (uma pagina por problema);
//   Evidencia — a prova (consulta com o plano, deadlock com as duas pontas).
// Estado de navegacao na URL (?aba=&problema=&consulta=&objeto=&dia=...): o
// "voltar" do navegador funciona e qualquer tela pode ser mandada por link.

const ABAS = [
  { chave: 'resumo', rotulo: 'Resumo', icone: 'bi-speedometer2' },
  { chave: 'problemas', rotulo: 'Problemas', icone: 'bi-list-check' },
  { chave: 'consultas', rotulo: 'Consultas', icone: 'bi-lightning-charge' },
  { chave: 'deadlocks', rotulo: 'Deadlocks', icone: 'bi-sign-stop' },
  { chave: 'servidor', rotulo: 'Servidor', icone: 'bi-hdd-rack' },
];

export default function AnaliseBanco() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [problemas, setProblemas] = useState(null);
  const [erro, setErro] = useState('');

  const aba = ABAS.some((a) => a.chave === params.get('aba')) ? params.get('aba') : 'resumo';
  const problemaId = params.get('problema');
  const consultaAberta = params.get('consulta');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/analise-banco/resumo`, { params: { dias: 90 } }),
      axios.get(`${API}/analise-banco/problemas`),
    ])
      .then(([r, p]) => { setDados(r.data.data); setProblemas(p.data.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.message || 'Não foi possível carregar a análise do banco.'));
  }, []);

  // Navegacao: sempre por aqui, para a URL ser a fonte do estado.
  const ir = (novos, { substituir = false } = {}) => {
    const p = new URLSearchParams();
    Object.entries(novos).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') p.set(k, v); });
    setParams(p, { replace: substituir });
    if (!substituir) window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const comConsulta = (id) => {
    const p = new URLSearchParams(params);
    if (id) p.set('consulta', id); else p.delete('consulta');
    setParams(p);
  };
  const abrirProblema = (p) => {
    const existe = problemas?.itens?.some((i) => i.id === p.id);
    if (existe) ir({ aba: 'problemas', problema: p.id });
    else if (p.fallback) p.fallback();
    else if (p.link?.consulta) comConsulta(p.link.consulta);
  };
  const relatorio = (tipo) => navigate(`/ti/analise-banco/relatorio/${tipo}`);

  const ligado = dados?.coletas?.cache?.sql_ligado_desde;
  const copiado = dados?.coletas?.cache?.iniciada_em;
  const problemaAtual = problemaId ? problemas?.itens?.find((i) => i.id === problemaId) : null;
  const abaAtual = ABAS.find((a) => a.chave === aba);

  return (
    <div className="content-shell ab">
      <section className="page-header ab-cabecalho">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Análise do Banco de Dados</h1>
          <p>O que está deixando o SQL Server lento, onde e quando, e quem resolve: a Microuniverso (ERP) ou nós (portais).</p>
        </div>
        {dados && (
          <div className="ab-cabecalho__fonte">
            <span><i className="bi bi-database-check" /> dados até <strong>{dataHora(copiado)}</strong></span>
            <small className="cell-muted">servidor ligado desde {dataHora(ligado)} ({diasDesde(ligado)} dias)</small>
          </div>
        )}
      </section>

      <nav className="ab-abas" aria-label="Seções da análise">
        {ABAS.map((a) => (
          <button key={a.chave} type="button" onClick={() => ir({ aba: a.chave })}
            className={`btn-portal ${aba === a.chave ? 'btn-portal--primary' : 'btn-portal--outline'}`}>
            <i className={`bi ${a.icone}`} /> {a.rotulo}
          </button>
        ))}
      </nav>

      {aba !== 'resumo' && (
        <nav className="ab-trilha" aria-label="Você está em">
          <button type="button" className="ab-link" onClick={() => ir({ aba: 'resumo' })}>Resumo</button>
          <i className="bi bi-chevron-right" />
          {problemaId ? (
            <>
              <button type="button" className="ab-link" onClick={() => ir({ aba: 'problemas' })}>Problemas</button>
              <i className="bi bi-chevron-right" />
              <span>{problemaAtual?.tituloCurto || 'Problema'}</span>
            </>
          ) : <span>{abaAtual?.rotulo}</span>}
        </nav>
      )}

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}
      {!dados && !erro && <Carregando />}

      {dados && aba === 'resumo' && (
        <Resumo
          dados={dados}
          problemas={problemas}
          onAbrirProblema={abrirProblema}
          onVerConsultas={(o) => ir({ aba: 'consultas', origem: o })}
          onVerDia={(d) => ir({ aba: 'deadlocks', dia: d })}
          onAba={(a, extra) => ir({ aba: a, ...extra })}
          onRelatorio={relatorio}
        />
      )}
      {dados && aba === 'problemas' && !problemaId && (
        <ListaProblemas problemas={problemas} acaoInicial={params.get('acao') || ''} onAbrir={abrirProblema} />
      )}
      {dados && aba === 'problemas' && problemaId && (
        <ProblemaDetalhe id={problemaId} onVoltar={() => navigate(-1)} onAbrirConsulta={comConsulta} onRelatorio={relatorio} />
      )}
      {dados && aba === 'consultas' && (
        <Consultas origemInicial={params.get('origem') || ''} onAbrir={comConsulta} cpuServidor={dados.cpuTotal} />
      )}
      {dados && aba === 'deadlocks' && (
        <Deadlocks
          filtroInicial={{ objeto: params.get('objeto'), dia: params.get('dia') }}
          onFiltro={({ objeto, dia }) => {
            if ((params.get('objeto') || null) === objeto && (params.get('dia') || null) === dia) return;
            ir({ aba: 'deadlocks', objeto, dia }, { substituir: true });
          }}
          onAbrirProblema={abrirProblema}
        />
      )}
      {dados && aba === 'servidor' && <Servidor dados={dados} />}

      {consultaAberta && (
        <ModalConsulta id={consultaAberta} onClose={() => comConsulta(null)}
          tamanhoBancoGb={dados?.config?.['banco.BDENTER.tamanho_gb']} cpuTotal={dados?.cpuTotal} />
      )}
    </div>
  );
}
